<template>
  <div class="wrapper">
    <div v-if="isRollupSource" class="alert alert-warning mx-2 mt-2 mb-0">
      <strong>Experimental — data from the new rollup endpoint.</strong>
      RPM PORT/STARBOARD channel labels do not match the current endpoint and are
      unverified. Do not use engine-side figures from this tab for reporting.
    </div>
    <div class="px-2">
      <data-filter :on-apply-filter="onApplyFilter" />
    </div>

    <template v-if="isLoading">
      <div class="text-center mt-5">
        <b-spinner label="Spinning"></b-spinner>
      </div>
    </template>

    <template v-else-if="currAssetSummary && !isLoading">
      <ae-fuel-summary-redesign
        v-if="isNewAeFuelLayout"
        :curr-asset="currAsset"
        :curr-asset-summary="currAssetSummary"
        :raw-summary="rawSummary"
        :curr-asset-device-status="currAssetDeviceStatus"
        :is-ae-cons-hidden="isAeConsHidden"
      />

      <template v-else>
        <div class="flex-container">
          <div class="flex-item iq-card" style="width: 70%;">
            <div class="iq-card-header pt-2">
              <div class="iq-card-title">
                <h5 class="card-title">Main Engine Summary</h5>
              </div>
            </div>

            <div class="iq-card-body">
              <template v-if="hasRpm">
                <div class="px-3 pb-2">
                  <p class="mb-0">
                    <b>RPM Sensors:</b> {{ currAssetDeviceStatus.rpmTypes.length }}
                    ({{ rpmDisplayLabels.join(', ') }})
                  </p>
                </div>
                <div class="flex-container">
                  <table
                    class="table flex-item"
                    v-for="type in currAssetDeviceStatus.rpmTypes"
                    :key="type"
                  >
                    <thead class="thead-light">
                      <th>{{ capitalize(rpmLabel(type)) }} RPM Summary</th>
                      <th>Value</th>
                      <th>Unit</th>
                    </thead>
                    <tbody>
                      <tr>
                        <td><b>Average RPM</b></td>
                        <td>{{ rpmStat(type).average }}</td>
                        <td>RPM</td>
                      </tr>
                      <tr>
                        <td><b>Engine Running Hour</b></td>
                        <td>{{ rpmStat(type).runningTime }}</td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </template>
              <div
                class="flex-container"
                v-if="currAssetDeviceStatus.hasExhaustTemperature && exhaustRows.length"
              >
                <table class="table flex-item">
                  <thead class="thead-light">
                    <th>Exhaust Temperature Summary</th>
                    <th>Value</th>
                    <th>Unit</th>
                  </thead>
                  <tbody>
                    <tr v-for="row in exhaustRows" :key="row.key">
                      <td><b>Avg {{ row.label }} Exhaust Temperature</b></td>
                      <td>{{ row.value }}</td>
                      <td>°C</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-if="!hasRpm && !exhaustRows.length" class="no-device-note">No RPM device</div>
            </div>
          </div>
          <div
            class="flex-item iq-card"
            style="width: 30%;"
            v-if="currAssetDeviceStatus.aeCount > 0"
          >
            <div class="iq-card-header pt-2">
              <div class="iq-card-title">
                <h5 class="card-title">Auxiliary Engine Summary</h5>
              </div>
            </div>
            <div class="iq-card-body flex-container">
              <table class="table flex-item">
                <thead class="thead-light">
                  <th>Data Summary</th>
                  <th>Running Time</th>
                  <th v-if="!isAeConsHidden">Fuel Cons. (Constanta)</th>
                </thead>
                <tbody>
                  <tr v-for="i in currAssetDeviceStatus.aeCount" :key="i">
                    <td><b>AE {{ i }}</b></td>
                    <td>{{ aeStat(i).runningSeconds }}</td>
                    <td v-if="!isAeConsHidden">{{ aeStat(i).fuelConsumption }} Liter</td>
                  </tr>
                  <tr>
                    <td><b>Total</b></td>
                    <td><b>{{ currAssetSummary?.ae?.runningSeconds }}</b></td>
                    <td v-if="!isAeConsHidden"><b>{{ currAssetSummary?.ae?.fuelConsumption }} Liter</b></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div class="flex-container" v-if="flowmeterGroups.length > 0">
          <div class="flex-item iq-card" style="width: 70%;">
            <div class="iq-card-header pt-2">
              <div class="iq-card-title">
                <h5 class="card-title">Flowmeter Fuel Summary</h5>
              </div>
            </div>

            <div class="iq-card-body">
              <div>
                <div v-for="group in flowmeterGroups" :key="group.name" class="flex-item" style="width: 100%">
                  <table v-if="group.hasIn && group.hasOut" class="table">
                    <thead class="thead-light">
                      <th>{{ prettyName(group.name) }} Fuel Summary</th>
                      <th>In</th>
                      <th>Out</th>
                      <th>Net</th>
                      <th>Unit</th>
                    </thead>
                    <tbody>
                      <tr>
                        <td><b>Average Flow</b></td>
                        <td>{{ groupData(group.name).in && groupData(group.name).in.avgFuelFlow }}</td>
                        <td>{{ groupData(group.name).out && groupData(group.name).out.avgFuelFlow }}</td>
                        <td>{{ groupNetAvgFlow(group.name) }}</td>
                        <td>L/h</td>
                      </tr>
                      <tr>
                        <td><b>Total Volume</b></td>
                        <td>{{ groupData(group.name).in && groupData(group.name).in.engineCons }}</td>
                        <td>{{ groupData(group.name).out && groupData(group.name).out.engineCons }}</td>
                        <td>{{ groupNetVolume(group.name) }}</td>
                        <td>Liter</td>
                      </tr>
                    </tbody>
                  </table>
                  <table v-else class="table">
                    <thead class="thead-light">
                      <th>{{ prettyName(group.name) }} Fuel Summary</th>
                      <th>Value</th>
                      <th>Unit</th>
                    </thead>
                    <tbody>
                      <tr>
                        <td><b>Avg Flow</b></td>
                        <td>{{ groupData(group.name).in && groupData(group.name).in.avgFuelFlow }}</td>
                        <td>L/h</td>
                      </tr>
                      <tr>
                        <td><b>Total Volume</b></td>
                        <td>{{ groupData(group.name).in && groupData(group.name).in.engineCons }}</td>
                        <td>Liter</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div class="flex-container" v-if="hasReservoirTank">
                <table class="table flex-item">
                  <thead class="thead-light">
                    <th>Reservoir Tank Summary</th>
                    <th>Value</th>
                    <th>Unit</th>
                  </thead>
                  <tbody>
                    <tr v-for="row in tankRows" :key="row.key">
                      <td><b>Average {{ row.label }} Reservoir Temperature</b></td>
                      <td>{{ row.value }}</td>
                      <td>°C</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div class="flex-item iq-card" style="width: 30%;">
            <div class="iq-card-header pt-2">
              <div class="iq-card-title">
                <h5 class="card-title">Fuel Consumption Summary</h5>
              </div>
            </div>
            <div class="iq-card-body flex-container">
              <table class="table flex-item">
                <thead class="thead-light">
                  <th>Fuel Summary</th>
                  <th>Value</th>
                  <th>Unit</th>
                </thead>
                <tbody>
                  <tr>
                    <td><b>Total Fuel by Flowmeter</b></td>
                    <td>{{ totalFuelByFlowmeter }}</td>
                    <td>Liter</td>
                  </tr>
                  <tr>
                    <td><b>ME Fuel Cons</b></td>
                    <td>{{ currAssetSummary?.fm?.meEngineCons }}</td>
                    <td>Liter</td>
                  </tr>
                  <tr v-if="currAssetDeviceStatus.aeCount > 0 && !isAeConsHidden">
                    <td><b>AE Cons (by Constanta)</b></td>
                    <td>{{ currAssetSummary?.ae?.fuelConsumption }}</td>
                    <td>Liter</td>
                  </tr>
                  <tr>
                    <td><b>{{ isAeConsHidden ? 'Total (ME)' : 'Total (ME + AE Constanta)' }}</b></td>
                    <td><b>{{ totalMePlusAeConstanta }}</b></td>
                    <td><b>Liter</b></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </template>

      <div class="flex-container">
        <div class="flex-item map-section" style="width: 70%;">
          <Map v-if="currAsset" :ships="mapShips" SelectedMap="default" :center="center" :zoom="zoom"
            style="border-radius: 20px; margin-top: 10px; max-height: 100%;"
            :Options="{ enableTrail: true, disableSummaryFetch: true, compactPopup: true, trailRange: mapTrailRange }" />
          <p v-if="isRollupSource" class="text-muted small mb-0 mt-1 text-center">
            Map track comes from /summary/trail (hourly), not the rollup endpoint.
          </p>
        </div>
        <div class="flex-item iq-card" style="width: 30%;">
          <div class="iq-card-header pt-2">
            <div class="iq-card-title">
              <h5 class="card-title">GPS Summary</h5>
            </div>
          </div>
          <div class="iq-card-body">
            <table class="table flex-item">
              <tbody>
                <tr>
                  <th><b>Average Speed</b></th>
                  <td>{{ currAssetSummary?.gps?.averageSpeed }}</td>
                  <td>KNOT</td>
                </tr>
                <tr>
                  <th><b>Total Cruise</b></th>
                  <td>{{ currAssetSummary?.gps?.totalDistance }}</td>
                  <td>NM</td>
                </tr>
                <tr v-if="lastLocation">
                  <th><b>Last Location</b></th>
                  <td colspan="2">{{ lastLocation }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div class="flex-container" v-if="hasRpm || flowmeterGroups.length > 0">
        <div class="flex-item iq-card" style="width: 100%;">
          <div class="iq-card-header pt-2">
            <div class="iq-card-title">
              <h5 class="card-title">{{ rpmFuelSpeedTitle }}</h5>
            </div>
          </div>
          <div class="iq-card-body">
            <div class="row">
              <div
                class="col text-center"
                v-for="type in currAssetDeviceStatus.rpmTypes"
                :key="type"
              >
                <div>
                  <h5 class="">{{ rpmStat(type).average }}</h5>
                  <span class="">Avg. {{ capitalize(rpmLabel(type)) }} RPM</span>
                </div>
              </div>
              <div class="col text-center">
                <div>
                  <h5 class="">{{ currAssetSummary?.gps?.averageSpeed }} Knot</h5>
                  <span class="">Avg. Speed</span>
                </div>
              </div>
            </div>
            <div class="report-chart-scroll">
              <rpm-fuel-speed
                :series="currAssetSummary.series"
                :deviceStatus="currAssetDeviceStatus"
              />
              <p v-if="isRollupSource" class="text-muted small mb-0 mt-1 text-center">
                Chart data from /series/aggregated (device telemetry), not the rollup endpoint.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div
        class="flex-container"
        v-if="canRead('summary_operation_mode')
          && (isOpModeLoading || Object.keys(engineOpModeSummaries).length > 0)"
      >
        <div class="flex-item iq-card" style="width: 100%;">
          <div class="iq-card-header pt-2">
            <div class="iq-card-title">
              <h5 class="card-title">RPM Classification Summary</h5>
            </div>
          </div>
          <div v-if="isOpModeLoading" class="iq-card-body text-center py-4">
            <b-spinner small label="Spinning"></b-spinner>
          </div>
          <div v-else class="iq-card-body">
            <div class="row">
              <div class="col-md-6 mb-4" v-for="(summaryData, engineName) in engineOpModeSummaries" :key="engineName">
                <div class="iq-card border shadow-none h-100">
                  <div class="iq-card-header pt-2 bg-light">
                    <div class="iq-card-title">
                      <h5 class="card-title text-capitalize">{{ engineName }}</h5>
                    </div>
                  </div>
                  <div class="iq-card-body p-0">
                    <table class="table table-striped table-bordered mb-0">
                      <thead class="thead-light">
                        <tr>
                          <th class="text-center">RPM Classification</th>
                          <th class="text-center">Duration</th>
                          <th class="text-center">Percentage</th>
                          <th class="text-center">Fuel Consumption</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(item, index) in summaryData" :key="index">
                          <td class="text-center">{{ item.mode }}</td>
                          <td class="text-center">{{ item.duration }}</td>
                          <td class="text-center">{{ item.percentage }}</td>
                          <td class="text-center">{{ item.consumption }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        class="flex-container"
        v-if="currAssetDeviceStatus.hasPowermeter && currAssetSummary.powermeter.length > 0"
      >
        <div class="flex-item iq-card" style="width: 100%;">
          <div class="iq-card-header pt-2">
            <div class="iq-card-title">
              <h5 class="card-title">Powermeter Summary</h5>
            </div>
          </div>
          <div class="iq-card-body">
            <table class="table">
              <thead class="thead-light">
                <th>Name</th>
                <th>Total Energy</th>
                <th>Avg. Active Power</th>
                <th>Avg. Frequency</th>
                <th>Avg. Voltage</th>
                <th>Avg. Current</th>
                <th>Avg. Power Factor</th>
              </thead>
              <tbody>
                <tr v-for="(pm, index) in currAssetSummary.powermeter" :key="index">
                  <td><b>{{ pm.name }}</b></td>
                  <td>{{ pm.totalEnergy }} kWh</td>
                  <td>{{ pm.avgActivePower }} kW</td>
                  <td>{{ pm.avgFrequency }} Hz</td>
                  <td>{{ pm.avgVoltage }} V</td>
                  <td>{{ pm.avgCurrent }} A</td>
                  <td>{{ pm.avgPowerFactor }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div
        v-for="group in flowmeterGroups"
        :key="group.name"
        class="flex-container"
      >
        <div class="flex-item iq-card" style="width: 100%;">
          <div class="iq-card-header pt-2">
            <div class="iq-card-title">
              <h5 class="card-title">{{ prettyName(group.name) }} Fuel Characteristics</h5>
            </div>
          </div>
          <div class="iq-card-body">
            <div class="row">
              <div class="col text-center" v-if="isRpmPosition(group.name)">
                <div>
                  <h5 class="">{{ rpmStat(group.name).average }}</h5>
                  <span class="">Avg. {{ capitalize(rpmLabel(group.name)) }} RPM</span>
                </div>
              </div>
              <div class="col text-center">
                <div v-if="group.hasOut">
                  <div class="row">
                    <div class="col-6">
                      <h5 class="">{{ groupData(group.name).in && groupData(group.name).in.avgFuelFlow }} L/h</h5>
                      <span class="">Avg. Flowrate In</span>
                    </div>
                    <div class="col-6">
                      <h5 class="">{{ groupData(group.name).out && groupData(group.name).out.avgFuelFlow }} L/h</h5>
                      <span class="">Avg. Flowrate Out</span>
                    </div>
                  </div>
                </div>
                <div v-else>
                  <h5 class="">{{ groupData(group.name).in && groupData(group.name).in.avgFuelFlow }} L/h</h5>
                  <span class="">Avg. {{ prettyName(group.name) }} Flowrate</span>
                </div>
              </div>
              <div class="col text-center">
                <div v-if="group.hasOut">
                  <div class="row">
                    <div class="col-6">
                      <h5 class="">{{ groupData(group.name).in && groupData(group.name).in.avgDensity }} g/cm³</h5>
                      <span class="">Avg. Density In</span>
                    </div>
                    <div class="col-6">
                      <h5 class="">{{ groupData(group.name).out && groupData(group.name).out.avgDensity }} g/cm³</h5>
                      <span class="">Avg. Density Out</span>
                    </div>
                  </div>
                </div>
                <div v-else>
                  <h5 class="">{{ groupData(group.name).in && groupData(group.name).in.avgDensity }} g/cm³</h5>
                  <span class="">Avg. {{ prettyName(group.name) }} Density</span>
                </div>
              </div>
            </div>
            <div class="report-chart-scroll">
              <fuel-characteristics
                :series="currAssetSummary.series"
                :type="group.name"
                :deviceStatus="currAssetDeviceStatus"
              />
              <p v-if="isRollupSource" class="text-muted small mb-0 mt-1 text-center">
                Chart data from /series/aggregated (device telemetry), not the rollup endpoint.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div
        class="flex-container"
        v-if="currAssetDeviceStatus.aeCount > 0"
      >
        <div class="flex-item iq-card" style="width: 100%;">
          <div class="iq-card-header pt-2">
            <div class="iq-card-title">
              <h5 class="card-title">Auxiliary Engine Usage</h5>
            </div>
          </div>
          <div class="iq-card-body">
            <div class="row">
              <div
                class="col text-center"
                v-for="i in currAssetDeviceStatus.aeCount"
                :key="'run-' + i"
              >
                <div>
                  <h5 class="">{{ aeStat(i).runningSeconds }}</h5>
                  <span class="">AE {{ i }} Running Hours</span>
                </div>
              </div>
            </div>
            <div class="row" v-if="!isAeConsHidden">
              <div
                class="col text-center"
                v-for="i in currAssetDeviceStatus.aeCount"
                :key="'fuel-' + i"
              >
                <div>
                  <h5 class="">{{ aeStat(i).fuelConsumption }} Liter</h5>
                  <span class="">AE {{ i }} Consumption (Constanta)</span>
                </div>
              </div>
            </div>
            <div class="report-chart-scroll">
              <auxiliary-engine-usage
                :series="currAssetSummary.series"
                :deviceStatus="currAssetDeviceStatus"
                :hideFuel="isAeConsHidden || isRollupSource"
              />
              <p v-if="isRollupSource" class="text-muted small mb-0 mt-1 text-center">
                AE fuel series unavailable from /series/aggregated — running hours only.
                Chart data is device telemetry, not the rollup endpoint.
              </p>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template v-else>
      <p class="text-muted text-center my-5">
        No summary data for this asset and range.
      </p>
    </template>
  </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import { mapState, mapActions } from 'vuex';
import Map from '../map/index.vue';
// import { LatLng } from 'leaflet';
import { buildFmsSummary, getRpmTypeLabel, getIntervalSeconds } from '../../util/fms-summary';
import { prettyLabel, locationDescription } from '../../util/vesselDetail';
import { initAssetSummary, applyAssetSummaryFilter } from '../../util/asset-summary-loader';
import isAeConsumptionHidden, { isAeFuelTableRedesigned } from '../../util/asset-feature-flags';
import DataFilter from '../../components/DataFilter';
import AuxiliaryEngineUsage from '../../components/charts/AuxiliaryEngineUsage';
import OperationModeSvc from '../../services/OperationModeSvc';
import RpmFuelSpeed from '../../components/charts/RpmFuelSpeed';
import FuelCharacteristics from '../../components/charts/FuelCharacteristics';
import AeFuelSummaryRedesign from '../../components/report/AeFuelSummaryRedesign';
import SummarySvc from '../../services/SummarySvc';
import SeriesSvc from '../../services/SeriesSvc';
import { seriesToSummaryData, buildChunkRanges } from '../../util/seriesToSummaryData';
import { toDuration } from '../../util/date';

export default {
  name: 'FmsReport',
  components: {
    Map,
    DataFilter,
    RpmFuelSpeed,
    FuelCharacteristics,
    AuxiliaryEngineUsage,
    AeFuelSummaryRedesign,
  },
  props: {
    // 'legacy' = summary dari Vuex (diisi asset/fetchSummary, endpoint lama).
    // 'rollup' = summary dari /summary/asset/period, disimpan lokal.
    summarySource: {
      type: String,
      default: 'legacy',
      validator: (value) => ['legacy', 'rollup'].includes(value),
    },
  },
  data() {
    return {
      // eslint-disable-next-line no-undef
      center: L.LatLng(0, 0),
      zoom: 10,
      numbers: [],
      masks: {
        input: 'DD-MM-YYYY HH:mm',
      },
      isLoading: true,
      appliedDateRange: null,
      appliedInterval: null,
      appliedMassId: null,
      opModeSummary: {},
      rollupSummary: null,
      isOpModeLoading: false,
      // Naik tiap load. Yang nyusul di belakang (opMode, trail) cek ini sebelum
      // nulis -- kalo user keburu Apply lagi, hasil lama gak boleh nimpa yang baru.
      loadSeq: 0,
    };
  },
  created() {
    for (let i = 600; i <= 1300; i += 100) {
      this.numbers.push(i);
    }
  },
  async mounted() {
    this.loadSeq += 1;
    const date = this.$store.getters['filter/date'];
    try {
      await initAssetSummary(this.$store, {
        dateRange: date,
        allowMinute: false,
        withDevices: true,
        withLastData: false,
        skipSummary: this.isRollupSource,
      });
      this.appliedDateRange = {
        start: new Date(moment(date.start).valueOf()),
        end: new Date(moment(date.end).valueOf()),
      };
      this.appliedInterval = this.$store.getters['filter/interval'];
      this.appliedMassId = this.$store.getters['filter/currentMassId'];
      if (this.summarySource === 'rollup') {
        await this.fetchRollupSummary();
      }
    } catch (err) {
      console.error(err);
      this.$toast.error('Failed to load report summary');
    } finally {
      this.isLoading = false;
    }
    // Sengaja TANPA await dan di luar try: summary udah tampil, sisanya nyusul
    // sambil nunjukin loading-nya sendiri. Kalo di-await, halaman bengong nungguin
    // yang bukan isi utamanya.
    this.fetchOperationModeSummary();
  },
  computed: {
    currAsset() {
      const currMassId = this.appliedMassId || this.$store.getters['filter/currentMassId'];
      const assets = this.$store.getters['asset/assets'];
      if (!assets) return null;

      return assets.find((asset) => asset.massId === currMassId);
    },
    // Satu-satunya titik baca data mentah summary di komponen ini.
    // Tab rollup pakai state lokal, bukan slot Vuex, biar dua tab gak saling
    // timpa asset.assets[].summary.
    rawSummary() {
      if (this.summarySource === 'rollup') return this.rollupSummary;
      return this.currAsset?.summary || null;
    },
    isRollupSource() {
      return this.summarySource === 'rollup';
    },
    // Tab rollup gak nembak /summary/aggregated/period, jadi asset di Vuex gak
    // punya trail -- jejaknya dari /summary/trail, posisi terakhir dari rollup.
    // lastLatLng objek biasa, bukan L.latLng: mapService udah baca dua-duanya,
    // dan tanpa itu createShipMarker balik null (trail ikut ilang, nempel di
    // layer group punya marker).
    // Trail ngikut rentang filter halaman, bukan "N hari dari data terakhir".
    mapTrailRange() {
      const r = this.appliedDateRange || this.dateRange;
      if (!r?.start || !r?.end) return undefined;
      return { start: moment(r.start).unix(), end: moment(r.end).unix() };
    },
    mapShips() {
      if (!this.currAsset) return [];
      if (!this.isRollupSource) return [this.currAsset];

      // Jejaknya ditarik MapService lewat fetchShipTrail — satu-satunya penulis
      // `coordinates`. Di sini cukup nempelin posisi terakhir, soalnya endpoint
      // rollup ngasih lastPosition tapi gak ngasih deret jejaknya.
      const ship = { ...this.currAsset };
      const last = this.rollupSummary?.gps?.summary?.lastPosition;
      if (last?.latitude != null && last?.longitude != null) {
        const points = ship.coordinatesData || [];
        const tail = points[points.length - 1];
        ship.lastLatLng = { lat: last.latitude, lng: last.longitude };
        ship.lastGpsData = {
          timestamp: last.timestamp,
          speed: tail?.gpsData?.speed,
          track: tail?.gpsData?.track,
        };
      }
      return [ship];
    },
    currAssetDeviceStatus() {
      // Report standalone dari response summary API (ga pake pulse/last data).
      // flowmeterGroups & hasExhaustTemperature dari device list; rpmTypes & aeCount
      // diturunin dari summary biar konsisten sama data yang beneran di-fetch di sini.
      const base = this.currAsset?.deviceStatus || {};
      const summary = this.rawSummary;
      return {
        ...base,
        rpmTypes: this.deriveRpmTypesFromSummary(summary),
        aeCount: this.deriveAeCountFromSummary(summary),
      };
    },
    hasRpm() {
      return (this.currAssetDeviceStatus?.rpmTypes || []).length > 0;
    },
    isAeConsHidden() {
      return isAeConsumptionHidden(this.currAsset?.massId);
    },
    isNewAeFuelLayout() {
      return isAeFuelTableRedesigned(this.currAsset?.massId);
    },
    flowmeterGroups() {
      return this.currAssetDeviceStatus?.flowmeterGroups || [];
    },
    exhaustRows() {
      // Exhaust temp lepas dari RPM: tampilkan posisi yang ada datanya (port/starboard/center).
      const ex = this.currAssetSummary?.temperature?.exhaust || {};
      return ['port', 'starboard', 'center']
        .filter((pos) => ex[pos] && ex[pos].average !== '-' && ex[pos].average !== undefined)
        .map((pos) => ({ key: pos, label: this.posLabel(pos), value: ex[pos].average }));
    },
    tankRows() {
      // Reservoir tank data-driven (termasuk center buat single-engine).
      const tank = this.currAssetSummary?.temperature?.tank || {};
      return ['port', 'starboard', 'center']
        .filter((pos) => tank[pos] && tank[pos].average !== '-' && tank[pos].average !== undefined)
        .map((pos) => ({ key: pos, label: this.posLabel(pos), value: tank[pos].average }));
    },
    hasReservoirTank() {
      const hasDevice = (this.currAsset?.devices || []).some(
        (d) => d && d.devcLabel && d.devcLabel.toLowerCase() === 'temperature',
      );
      return hasDevice && this.tankRows.length > 0;
    },
    rpmDisplayLabels() {
      // Map RPM types to display labels (CENTER → MAIN if single type)
      const rpmTypes = this.currAssetDeviceStatus?.rpmTypes || [];
      return rpmTypes.map((type) => getRpmTypeLabel(type, rpmTypes));
    },
    rpmFuelSpeedTitle() {
      // Judul ngikut data yang benar-benar diplot (RPM hilang buat aset tanpa RPM).
      const parts = [];
      if (this.hasRpm) parts.push('RPM');
      if (this.flowmeterGroups.length > 0) parts.push('Fuel Consumption');
      parts.push('Speed');
      if (parts.length >= 3) {
        return `${parts.slice(0, -1).join(', ')}, And ${parts[parts.length - 1]} Chart`;
      }
      if (parts.length === 2) return `${parts[0]} And ${parts[1]} Chart`;
      return `${parts[0]} Chart`;
    },
    totalFuelByFlowmeter() {
      // Total fuel terukur = jumlah konsumsi semua line flowmeter (net dari backend),
      // mencakup ME (PORT/STBD/CENTER) maupun AE-line (AE4, AE_INLINE, ...).
      const groups = this.flowmeterGroups;
      const fmSummary = this.rawSummary?.flowmeter?.summary || {};
      let total = 0;
      let any = false;
      groups.forEach((g) => {
        const key = `${g.name.toLowerCase()}EngineCons`;
        const v = Number(fmSummary[key]);
        if (Number.isFinite(v)) {
          total += v;
          any = true;
        }
      });
      return any ? total.toFixed(2) : '-';
    },
    totalMePlusAeConstanta() {
      const me = parseFloat(this.currAssetSummary?.fm?.meEngineCons || 0);
      if (this.isAeConsHidden) return me.toFixed(2);
      const ae = parseFloat(this.currAssetSummary?.ae?.fuelConsumption || 0);
      return (me + ae).toFixed(2);
    },
    currAssetSummary() {
      const effectiveDateRange = this.appliedDateRange || this.dateRange;
      const effectiveInterval = this.appliedInterval || this.interval;
      const builtSummary = buildFmsSummary({
        deviceStatus: this.currAssetDeviceStatus,
        summary: this.rawSummary,
        dateRange: effectiveDateRange,
        interval: effectiveInterval,
        aeFuelFixed: true,
      });

      return builtSummary;
    },
    lastLocation() {
      return locationDescription({ locationInfo: this.currAssetSummary?.gps?.zone });
    },
    dateRange: {
      get() {
        const { start, end } = this.$store.getters['filter/date'];
        const startDate = new Date(moment(start).valueOf());
        const endDate = new Date(moment(end).valueOf());

        return { start: startDate, end: endDate };
      },
      set(val) {
        const start = moment(val.start);
        const end = moment(val.end);

        this.$store.commit('filter/SET_DATE', { start, end });
      },
    },
    ...mapState('asset', ['assets']),
    ...mapState('auth', ['userAccess']),
    ...mapState('filter', {
      currentMassId: (state) => state.currentMassId,
      interval: (state) => state.interval,
    }),
    selectedInterval: {
      get() {
        return this.interval;
      },
      set(val) {
        this.$store.dispatch('filter/setInterval', val);
      },
    },
    selectedAsset: {
      get() {
        return this.currentMassId;
      },
      set(val) {
        this.$store.dispatch('filter/setCurrentMassId', val);
      },
    },
    engineOpModeSummaries() {
      return this.opModeSummary;
    },
  },
  methods: {
    capitalize(s) {
      if (typeof s !== 'string') return '';
      return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
    },
    deriveRpmTypesFromSummary(summary) {
      // Tipe RPM (PORT/STARBOARD/CENTER) dari key summary RPM yang beneran ada datanya.
      const avg = summary?.rpm?.summary?.average || {};
      const running = summary?.rpm?.summary?.runningTime || {};
      const types = new Set([...Object.keys(avg), ...Object.keys(running)]);
      const order = ['PORT', 'STARBOARD', 'CENTER'];
      return Array.from(types).sort((a, b) => {
        const ia = order.indexOf(a);
        const ib = order.indexOf(b);
        return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
      });
    },
    deriveAeCountFromSummary(summary) {
      // aeCount = nomor AE tertinggi yang muncul di summary AE (AE1, AE2, ...).
      const fc = summary?.ae?.summary?.fuelConsumption || {};
      const rs = summary?.ae?.summary?.runningSeconds || {};
      let max = 0;
      [...Object.keys(fc), ...Object.keys(rs)].forEach((key) => {
        const match = /^AE(\d+)$/.exec(key);
        if (match) max = Math.max(max, Number(match[1]));
      });
      return max;
    },
    rpmLabel(type) {
      return getRpmTypeLabel(type, this.currAssetDeviceStatus?.rpmTypes || []);
    },
    rpmStat(type) {
      return this.currAssetSummary?.rpm?.[type] || {};
    },
    posLabel(pos) {
      // Label posisi konsisten sama RPM: single-engine CENTER -> "Main".
      const upper = String(pos).toUpperCase();
      return this.capitalize(getRpmTypeLabel(upper, this.currAssetDeviceStatus?.rpmTypes || []));
    },
    prettyName(name) {
      return prettyLabel(name);
    },
    groupData(name) {
      return this.currAssetSummary?.fm?.[name] || {};
    },
    groupNetAvgFlow(name) {
      const data = this.groupData(name);
      const inVal = parseFloat((data.in && data.in.avgFuelFlow) || 0);
      const outVal = parseFloat((data.out && data.out.avgFuelFlow) || 0);
      return (inVal - outVal).toFixed(2);
    },
    groupNetVolume(name) {
      const data = this.groupData(name);
      const inVal = parseFloat((data.in && data.in.engineCons) || 0);
      const outVal = parseFloat((data.out && data.out.engineCons) || 0);
      return (inVal - outVal).toFixed(2);
    },
    isRpmPosition(name) {
      return (this.currAssetDeviceStatus?.rpmTypes || []).includes(String(name).toUpperCase());
    },
    aeStat(i) {
      const ae = (this.currAssetSummary && this.currAssetSummary.ae) || {};
      return ae[`ae${i}`] || {};
    },
    ...mapActions(['updatekapalTrail']),
    zoomToShip(ship) {
      // eslint-disable-next-line no-undef
      this.center = L.LatLng(ship.position[0], ship.position[1]);
      this.zoom = 15;
    },
    async onApplyFilter() {
      this.loadSeq += 1;
      this.isLoading = true;
      try {
        await applyAssetSummaryFilter(this.$store, {
          dateRange: this.dateRange,
          interval: this.selectedInterval || this.interval,
          massId: this.selectedAsset || this.currentMassId,
          skipSummary: this.isRollupSource,
        });
        this.appliedDateRange = {
          start: new Date(moment(this.dateRange.start).valueOf()),
          end: new Date(moment(this.dateRange.end).valueOf()),
        };
        this.appliedInterval = this.interval;
        this.appliedMassId = this.currentMassId;
        if (this.summarySource === 'rollup') {
          await this.fetchRollupSummary();
        }
      } catch (err) {
        console.error(err);
        this.$toast.error('Failed to apply filter');
      } finally {
        this.isLoading = false;
      }
      this.fetchOperationModeSummary();
    },
    // Ambil deret waktu buat chart dari /series/aggregated (telemetri per-device).
    // Endpoint rollup sengaja gak ngirim deret waktu, jadi chart diisi dari sini.
    // Hasilnya dibentuk biar sama persis kayak blok `data` punya endpoint lama.
    //
    // Endpoint ini punya batas KERAS 6000 BARIS dan motong diam-diam kalau kena
    // (terverifikasi: 90 hari flowmeter 3 device @HOUR = 6.480 baris diharapkan,
    // yang balik cuma 6.000, tanpa error). Baris = bucket x channel, jadi rentang
    // panjang dipecah per tipe berdasarkan anggaran baris.
    //
    // Potongan dijalanin BERURUTAN, bukan paralel. Chunking paralel pernah bikin
    // 93 request barengan dan 29x HTTP 504 di halaman voyage sampai backend minta
    // FE menghapusnya (memo 2026-07-18). Yang paralel cuma keempat tipenya.
    //
    // `channels` WAJIB dikasih dari luar, jangan diganti balik ke
    // `this.currAssetDeviceStatus`: computed itu turunan dari `this.rollupSummary`
    // (lewat `rawSummary`), dan pas method ini jalan `rollupSummary` masih null
    // (baru diisi setelah method ini selesai) — jadi rpmTypes/aeCount-nya bakal
    // selalu kosong, chunk jadi 2x kegedean, dan limit 6000 kena diam-diam lagi.
    async fetchRollupChartSeries(start, end, interval, channels) {
      const devices = this.currAsset?.devices || [];
      const uidsOf = (t) => devices
        .filter((d) => d.devcType === t)
        .map((d) => d.devcUniqueId);

      // Berapa baris yang dihasilkan SATU bucket, per tipe. Ini pengali yang
      // nentuin kapan limit kena, dan dia BUKAN jumlah device: satu device rpm
      // ngasih satu baris per channel (PORT+STARBOARD), satu device ae ngasih
      // satu baris per AE. Angkanya dari `channels`, dihitung caller dari body
      // summary yang baru di-fetch.
      const rowsPerBucket = {
        gps: Math.max(uidsOf('gps').length, 1),
        rpm: Math.max((channels?.rpmTypes || []).length, 1),
        flowmeter: Math.max(uidsOf('flowmeter').length, 1),
        ae: Math.max(Number(channels?.aeCount) || 0, 1),
      };

      const intervalSeconds = getIntervalSeconds(interval) || 3600;
      // 6000 batas kerasnya; dipakai 80% biar ada ruang kalau ada channel yang
      // baru mulai ngirim di tengah rentang.
      const rowBudget = Math.floor(6000 * 0.8);

      // Ditiru dari store/asset.js:781 — jalur legacy kirim aggregatedUnit doang,
      // tanpa aggregatedLength. Kalau beda, angka chart dua tab gak sepadan.
      const base = {
        aggregatedUnit: String(interval).toUpperCase(),
        timezone: new Date().getTimezoneOffset() / -60,
      };

      // Tipe yang gagal atau kepotong limit — dilaporkan lewat satu toast
      // setelah keempat fetch selesai, bukan console.warn doang.
      const failedTypes = [];

      const fetchType = async (type, extra) => {
        const device = uidsOf(type);
        if (!device.length) return [];

        const maxBuckets = Math.max(Math.floor(rowBudget / rowsPerBucket[type]), 1);
        const chunks = buildChunkRanges(start, end, maxBuckets * intervalSeconds);
        const rows = [];

        for (let i = 0; i < chunks.length; i += 1) {
          try {
            // Sengaja berurutan — lihat catatan 504 di atas.
            // eslint-disable-next-line no-await-in-loop
            const res = await SeriesSvc.getAggregatedDataByPeriod(
              type, chunks[i].start, chunks[i].end, { params: { device, ...base, ...extra } },
            );
            const body = res?.data?.data || {};
            rows.push(...(body.series || []));
            if (body.limit && body.count >= body.limit) {
              // Data separuh lebih menyesatkan daripada chart kosong: chart yang
              // cuma memuat sebagian rentang tetap kelihatan wajar. Buang semuanya.
              console.warn(`[rollup chart] ${type}: satu potongan masih kena limit ${body.limit} — perhitungan chunk meleset, data kemungkinan terpotong`);
              failedTypes.push(type);
              return [];
            }
          } catch (e) {
            // Navigasi keluar mid-fetch (cancelPendingRequests) itu aksi biasa,
            // bukan kegagalan — jangan warn, jangan masuk toast.
            if (!axios.isCancel(e)) {
              console.warn(`[rollup chart] ${type}: potongan ${i + 1}/${chunks.length} gagal, chart tipe ini dikosongkan`, e);
              failedTypes.push(type);
            }
            return [];
          }
        }
        return rows;
      };

      // Keempat tipe boleh paralel (maksimal 4 request barengan). Yang dilarang
      // itu potongan-potongannya yang paralel.
      const [gps, rpm, flowmeter, ae] = await Promise.all([
        // gps & flowmeter sengaja TANPA functionNames: endpoint lama naruh
        // sampel dasar di data[ts].speed dan data[ts].volumeFlowrate, bukan
        // rata-rata (terverifikasi cocok persis di 4 bucket). rpm sebaliknya,
        // dia pakai AVGGT (rata-rata pas mesin nyala, sesuai yang dicharting
        // endpoint legacy). Jangan diseragamkan — angkanya bakal bergeser.
        fetchType('gps', {}),
        fetchType('rpm', { functionNames: ['AVGGT'], functionParams: ['@Rpm'] }),
        fetchType('flowmeter', {}),
        fetchType('ae', { functionNames: ['SUM'], functionParams: ['@Rpm'] }),
      ]);

      if (failedTypes.length > 0) {
        const order = ['gps', 'rpm', 'flowmeter', 'ae'];
        const names = order.filter((t) => failedTypes.includes(t));
        this.$toast.error(`Chart data unavailable for: ${names.join(', ')}. Try a shorter range or a coarser interval.`);
      }

      const labelByUid = {};
      devices
        .filter((d) => d.devcType === 'flowmeter' && d.devcLabel)
        .forEach((d) => { labelByUid[d.devcUniqueId] = d.devcLabel; });

      return {
        gps: seriesToSummaryData('gps', gps),
        rpm: seriesToSummaryData('rpm', rpm),
        flowmeter: seriesToSummaryData('flowmeter', flowmeter, labelByUid),
        ae: seriesToSummaryData('ae', ae),
      };
    },
    // Ambil summary dari endpoint rollup. Cuma dipanggil pas summarySource
    // 'rollup'; hasilnya masuk state lokal, gak nyentuh Vuex.
    async fetchRollupSummary() {
      this.rollupSummary = null;
      const massId = this.appliedMassId || this.currentMassId;
      if (!massId) return;

      const range = this.appliedDateRange || this.$store.getters['filter/date'];
      // Jendelanya WAJIB sama persis sama yang dipakai jalur legacy. Lihat
      // asset-summary-loader.js:117-118 -- dia fetch pakai startOf(interval) dan
      // endOf('minute'), bukan rentang mentah. Kalau di sini gak dibulatin sama,
      // dua tab nembak jendela beda dan seluruh perbandingan angkanya gak sah.
      const interval = this.appliedInterval || this.interval;
      const rollupStart = moment(range.start).startOf(interval).unix();
      const rollupEnd = moment(range.end).endOf('minute').unix();
      const seq = this.loadSeq;
      const response = await SummarySvc.getAssetSummaryRollup({
        // Sengaja lebih sempit dari jalur legacy (yang derive per-asset dari devices).
        // temperature belum dilaporkan di sini -- endpoint rollup ngasihnya lewat
        // meta.unavailable. Kalau nanti rollup mulai serve temperature, tambahin di sini juga.
        devcType: ['gps', 'rpm', 'flowmeter', 'ae'],
        devcMassId: massId,
        start: rollupStart,
        end: rollupEnd,
      });
      const body = response?.data?.data || null;
      this.rollupSummary = body;
      if (!body) return;

      // Chart nyusul tanpa di-await, sama kayak trail. Hasilnya digabung lalu
      // ditugaskan SEKALI: nambah key `data` ke objek yang udah reaktif gak
      // kebaca Vue 2. rpmTypes/aeCount diturunin dari `body`, bukan dari
      // currAssetDeviceStatus -- kalau salah, chunk kegedean dan limit 6000
      // kena diam-diam.
      this.fetchRollupChartSeries(rollupStart, rollupEnd, interval, {
        rpmTypes: this.deriveRpmTypesFromSummary(body),
        aeCount: this.deriveAeCountFromSummary(body),
      }).then((chartData) => {
        if (seq !== this.loadSeq) return;
        const merged = {};
        Object.keys(body).forEach((type) => {
          merged[type] = chartData[type]
            ? { ...body[type], data: chartData[type] }
            : body[type];
        });
        this.rollupSummary = merged;
      });
    },
    async fetchOperationModeSummary() {
      this.opModeSummary = {};
      const asset = this.currAsset;

      if (!asset) return;

      const rpmDevice = asset.devices && asset.devices.find((d) => d.devcType === 'rpm');

      if (!rpmDevice) {
        return;
      }

      const { start, end } = this.dateRange;
      const startTime = moment(start).unix();
      const endTime = moment(end).unix();
      const seq = this.loadSeq;
      this.isOpModeLoading = true;

      try {
        const response = await OperationModeSvc.getOperationModeSummary({
          start: startTime,
          end: endTime,
          devcId: rpmDevice.devcId,
        });

        if (seq !== this.loadSeq) return;

        if (response.data.status?.errorCode === 412) {
          this.$toast.error(response.data.status.message || 'You don\'t have access to this vessel\'s operation mode data.');
          this.opModeSummary = {};
          return;
        }

        const summaryData = response.data.data;
        const processSummary = (modes, totalRunningSeconds) => {
          if (!modes) return [];
          return Object.keys(modes).map((modeKey) => {
            const modeData = modes[modeKey];
            const percentage = totalRunningSeconds > 0
              ? ((modeData.runningSeconds / totalRunningSeconds) * 100).toFixed(2)
              : 0;
            return {
              mode: modeKey,
              duration: toDuration(modeData.runningSeconds),
              percentage: `${percentage}%`,
              consumption: (modeData.value || 0).toFixed(2),
            };
          });
        };

        const processedSummary = {};
        if (summaryData.engine) {
          Object.keys(summaryData.engine).forEach((engineKey) => {
            const engineModes = summaryData.engine[engineKey];
            const engineTotalSeconds = Object.values(engineModes).reduce((acc, mode) => acc + mode.runningSeconds, 0);
            processedSummary[engineKey] = processSummary(engineModes, engineTotalSeconds);
          });
        }
        this.opModeSummary = processedSummary;
      } catch (error) {
        if (seq !== this.loadSeq) return;
        console.error('Failed to fetch operation mode summary:', error);
        this.opModeSummary = {};
        this.$toast.error('Failed to load RPM classification summary');
      } finally {
        if (seq === this.loadSeq) this.isOpModeLoading = false;
      }
    },
  },
};

</script>

<style scoped>
.flex-container {
  display: flex;
  justify-content: space-between;
  margin: 0px 10px;
}

.flex-item {
  margin: 10px 5px;
}

.table {
  width: 100%;
  table-layout: fixed;
}

.table th,
.table td {
  width: 33.33%;
  padding: 8px;
}

.no-device-note {
  padding: 24px 16px;
  color: #6b7280;
  font-style: italic;
  text-align: center;
}

.map-section {
  min-height: 600px;
}

.report-chart-scroll {
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
}

@media screen and (max-width: 991px) {
  .flex-container {
    flex-direction: column;
  }

  .flex-item {
    width: 100% !important;
    margin: 10px 0px !important;
  }

  .map-section {
    height: 300px;
    min-height: 0;
  }
}

@media screen and (max-width: 575px) {
  .iq-card-body {
    padding-left: 12px;
    padding-right: 12px;
  }

  .iq-card-body > .row {
    gap: 10px 0;
  }

  .iq-card-body > .row > .col {
    flex: 0 0 50%;
    max-width: 50%;
  }

  .iq-card-body > .row h5 {
    font-size: 1rem;
    margin-bottom: 2px;
  }

  .iq-card-body > .row span {
    font-size: 0.75rem;
    line-height: 1.2;
  }
}
</style>
